import { captureException } from '@sentry/react';
import { AccountsService, PlanName } from 'base/accounts_service';
import { AnalyticsService } from 'base/analytics';
import { NoProjectsEmptyState } from 'base/extensions/projects_panel';
import {
  fakeEmptyLibraryService,
  fakeLibraryService,
} from 'base/fake_library_service';
import { delay } from 'base/helpers';
import { Button } from 'base/ui/button';
import { Dialog, DisableDialogLayer_PlaygroundOnly } from 'base/ui/dialog';
import { IconCopyAi } from 'base/ui/icons';
import { Inline, Rows } from 'base/ui/layout';
import { PlaygroundSectionTitle } from 'base/ui/playground';
import { ToastLayer, ToastQueue } from 'base/ui/toast';
import { TextRegular, Title5 } from 'base/ui/typography';
import {
  ProFeatureGateDialog,
  TeamFeatureGateDialog,
} from 'blocks/billing/feature_gate_dialog';
import { GrowthNudge } from 'blocks/dashboard/growth_nudges';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { PaymentInfo } from './auth/accept_plan_offer';
import {
  CancelDiscountOfferDialog,
  CancelPlanDialog,
  ChurnFeedbackDialog,
} from './auth/cancel_plan';
import { SignupSurveyForm } from './auth/forms';
import { Comment, UserAvatar } from './blocks_service';
import {
  ErrorState,
  LoggedOutProjectsListState,
  TeamEmptyState,
  TeamUpsellCard as ProjectsListTeamUpsellCard,
} from './dashboard/projects_list/states';
import { SearchResults } from './dashboard/search';
import { AiTaskLog, LLMErrorFormatter } from './editor/ai/task_log';
import {
  installAiToasts,
  showAllPagesCelebrationToast,
  StoppableSpinner,
} from './editor/ai/toasts';
import { Canvas, CanvasViewport } from './editor/canvas';
import { Avatars } from './editor/commenting/avatars';
import { CommentPin } from './editor/commenting/pin';
import { EMPTY_PROJECT } from './editor/default_project';
import {
  ICONS_FOR_VARIANTS,
  VariantItemModel,
  VariantTogglesRow,
} from './editor/edit_panel/variants';
import { projectDomain } from './editor/model';
import { OnboardingDialog } from './editor/onboarding/dialog';
import { SurveyForm } from './editor/survey_form';
import { fakeBlocksService } from './fake_blocks_service';
import { KeyboardShortcutsDialog } from './keyboard_shortcuts_dialog';
import { CouponBanner } from './manage_account/coupon_banners';
import { TeamUpsellCard as ManageAccountTeamUpsellCard } from './manage_account/manage_team';
import styles from './playground.css';
import { ImportingDialogView } from './sitemap_import/sitemap_import_importing_dialog';
import { CounterClock } from './woot_woot/clock';
import { UndoManagerImpl } from './woot_woot/undo';

const analytics = new AnalyticsService({
  getToken: () => '',
  captureException,
});

function VariantsPlayground() {
  class FakeModel implements VariantItemModel {
    constructor(
      public groupKind: string,
      public label: string,
      public isSelected: boolean = false,
    ) {}

    handleClick = () => alert(`Clicked ${this.label}`);
    handleMouseEnter = () => console.log(`Mouse entered ${this.label}`);
    handleMouseLeave = () => console.log(`Mouse left ${this.label}`);
    handleUnmount = () => console.log(`Unmounted ${this.label}`);

    get icon() {
      return ICONS_FOR_VARIANTS[this.groupKind.toLowerCase()]?.[
        this.label.toLowerCase()
      ];
    }
  }

  // Look at the backend code to find what labels are allowed.
  const rows = [
    [
      new FakeModel('Text', 'Left', true),
      new FakeModel('Text', 'Center'),
      new FakeModel('Text', 'Right'),
    ],
    [
      new FakeModel('Asset Placement', 'Left', true),
      new FakeModel('Asset Placement', 'Right'),
      new FakeModel('Asset Placement', 'Top'),
      new FakeModel('Asset Placement', 'Bottom'),
    ],
    [new FakeModel('Style', 'Normal', true), new FakeModel('Style', 'Card')],
    [
      new FakeModel('Element', 'Form', true),
      new FakeModel('Element', 'Button'),
    ],
  ];

  return (
    <Rows spacing={16}>
      <PlaygroundSectionTitle label="Variants" />
      <div className={styles.variants}>
        <Rows spacing={8}>
          {rows.map((items, i) => (
            <VariantTogglesRow key={i} items={items} />
          ))}
        </Rows>
      </div>
    </Rows>
  );
}

function SignupFormPlayground() {
  return (
    <Rows spacing={16}>
      <PlaygroundSectionTitle label="Signup Form" />
      <SignupSurveyForm
        submitSignupSurveyForm={function (
          data: Record<string, string>,
        ): Promise<void> {
          throw new Error('Function not implemented.');
        }}
      />
    </Rows>
  );
}

function OnboardingPlayground() {
  const [open, setOpen] = React.useState(false);
  return (
    <Rows spacing={16}>
      <PlaygroundSectionTitle label="Onboarding" />
      <Button label="Show onboarding dialog" onClick={() => setOpen(true)} />
      {open && <OnboardingDialog onClose={() => setOpen(false)} mute={true} />}
    </Rows>
  );
}

function SearchPlayground() {
  const [toastQueue] = React.useState(() => new ToastQueue());
  const deps = {
    toastQueue,
    libraryService: fakeLibraryService,
    blocksService: fakeBlocksService,
    analytics,
  };
  return (
    <Rows spacing={16}>
      <PlaygroundSectionTitle label="Search" />
      <ToastLayer queue={toastQueue} />
      <div className={styles.searchContainer}>
        <SearchResults {...deps} mode="webflow" query="" />
      </div>
      <div className={styles.searchContainer}>
        <SearchResults {...deps} mode="webflow" query="Footers" />
      </div>
      <div className={styles.searchContainer}>
        <SearchResults
          {...deps}
          mode="webflow"
          query="Not Found"
          libraryService={fakeEmptyLibraryService}
        />
      </div>
      <div className={styles.searchContainer}>
        <SearchResults {...deps} mode="projects" query="" />
      </div>
    </Rows>
  );
}

function KeyboardShortcutsDialogPlayground() {
  return (
    <DisableDialogLayer_PlaygroundOnly.Provider value={true}>
      <Rows spacing={16}>
        <PlaygroundSectionTitle label="Keyboard Shortcuts Dialog" />
        <KeyboardShortcutsDialog onClose={() => alert('onClose')} />
      </Rows>
    </DisableDialogLayer_PlaygroundOnly.Provider>
  );
}

function CommentingPlayground() {
  const aComment: Comment = {
    id: 1,
    version: 1,
    threadId: 101,
    content: 'Make the heading copy shorter',
    createdAt: '2021-01-01T00:00:00Z',
    deleted: false,
    author: {
      id: 0,
      firstName: 'Sarah',
      lastName: 'Smith',
      color: '#06aed4',
    },
    isRead: true,
  };
  const author = (n: number): UserAvatar => ({
    id: n,
    firstName: n.toString(),
    lastName: 'Doe',
    color: ['#f63d68', '#f8b425', '#06aed4', '#00c9b7'][n % 4],
  });

  const avatarValues = [
    [aComment.author],
    [aComment.author, author(0)],
    [aComment.author, author(1), author(2), author(3), author(4)],
  ];

  return (
    <Rows spacing={16}>
      <PlaygroundSectionTitle label="Commenting" />
      {avatarValues.map((avatars, i) => (
        <div className={styles.commentPinContainer} key={i}>
          <CommentPin
            avatars={avatars}
            comment={aComment}
            numReplies={i}
            isSelected={true}
            isUnread={i === 0}
            x={50}
            y={50}
          />
          <CommentPin
            avatars={avatars}
            comment={aComment}
            numReplies={i}
            onClick={() => alert('clicked interactive comment')}
            isSelected={false}
            isUnread={i === 0}
            x={100}
            y={100}
          />
        </div>
      ))}
      <Avatars avatars={avatarValues[2]} max={6} isUnread={true} />
    </Rows>
  );
}

function SurveyPlayground() {
  const props = {
    onClose: () => alert('onClose'),
    config: {
      storageKey: 'playgroundSurveyForm',
      showAfterMinutes: 5,
      typeformUrl: 'https://example.com/',
    },
  };
  return (
    <Rows spacing={16}>
      <PlaygroundSectionTitle label="Survey" />
      <SurveyForm {...props} isNew={false} />
      <SurveyForm {...props} isNew={true} />
    </Rows>
  );
}

function ManageAccountPlayground() {
  return (
    <div className={styles.manageAccount}>
      <Rows spacing={16}>
        <PlaygroundSectionTitle label="Manage Account" />
        <ManageAccountTeamUpsellCard />
      </Rows>
    </div>
  );
}

function ProjectsListPlayground() {
  return (
    <Rows spacing={16}>
      <PlaygroundSectionTitle label="Projects List" />
      <ProjectsListTeamUpsellCard />
      <ErrorState error="Some error message" />
      <LoggedOutProjectsListState />
      <TeamEmptyState teamProjectsLabel="Sam Co projects" />
    </Rows>
  );
}

function CancelPlanDialogPlayground() {
  const cancelCouponOffer = {
    currentPeriodEndHuman: '11/11/2021',
    userFirstName: 'Bob',
    discountOfferPercent: 20,
    mtyOffer: {
      couponCode: 'Monthly To Yearly Coupon',
      discountPercent: 30,
      bannerUiName: undefined,
      planKey: 'proYearly',
      daysLeftToClaim: 0,
    },
  } as const;
  const accountsService = {
    getPausePlanOffer: async () => ({
      offerPause: true,
      currentPeriodEndHuman: '11/11/2021',
      resumeDateHuman: '12/12/2021',
    }),
    acceptPausePlanOffer: async () => ({}),
    getCancelCouponOffer: async () => cancelCouponOffer,
    acceptCancelCouponOffer: async () => ({}),
    saveChurnFeedback: async () => ({}),
    cancelPlanNow: async () => ({
      currentPeriodEndHuman: '11/11/2021',
    }),
  } satisfies Partial<AccountsService>;
  return (
    <DisableDialogLayer_PlaygroundOnly.Provider value={true}>
      <PlaygroundSectionTitle label="Cancel Plan UI" />
      <Rows spacing={32}>
        <TextRegular>All together (full flow):</TextRegular>
        <CancelPlanDialog
          onClose={() => alert('onClose (not done)')}
          onDone={() => alert('onDone')}
          onUpsell={(state) => alert(`onupsell: ${JSON.stringify(state)}`)}
          accountsService={accountsService}
        />
        <TextRegular>ChurnFeedbackDialog step:</TextRegular>
        <ChurnFeedbackDialog
          onClose={() => alert('onClose (not done)')}
          onDone={() => alert('onDone')}
          accountsService={accountsService}
        />
        <TextRegular>CancelDiscountOfferDialog step:</TextRegular>
        <CancelDiscountOfferDialog
          onClose={() => alert('onClose (not done)')}
          onDone={() => alert('onDone')}
          onUpsell={(state) => alert(`onupsell: ${JSON.stringify(state)}`)}
          onReject={() => alert('onReject cancel discount offers')}
          accountsService={accountsService}
          offers={cancelCouponOffer}
        />
      </Rows>
    </DisableDialogLayer_PlaygroundOnly.Provider>
  );
}

function PlansPlayground() {
  return (
    <DisableDialogLayer_PlaygroundOnly.Provider value={true}>
      <PlaygroundSectionTitle label="Plans UI" />
      <Rows spacing={32}>
        {[20, 1, 0].map((days) => (
          <CouponBanner
            key={days}
            coupon={{
              daysLeftToClaim: days,
              discountPercent: 20,
              bannerUiName: 'mty-2month',
              couponCode: '2 month monthly-to-yearly coupon',
              planKey: 'proYearly',
            }}
          />
        ))}
        <Dialog title="Signing up for a trial">
          <PaymentInfo
            trialPeriodDays={7}
            currentPaymentDateHuman="in a week"
            currentPayment={{ cents: 0, currency: 'usd', totalDiscount: 0 }}
            ongoingPayment={{ cents: 1500, currency: 'usd', totalDiscount: 0 }}
            ongoingPaymentDateHuman={'11/11/2021'}
            ongoingInterval={'month'}
            coupon={null}
          />
        </Dialog>
        <Dialog title="Paying today">
          <PaymentInfo
            trialPeriodDays={0}
            currentPaymentDateHuman="today"
            currentPayment={{ cents: 320, currency: 'usd', totalDiscount: 0 }}
            ongoingPayment={{
              cents: 1000,
              currency: 'usd',
              totalDiscount: 500,
            }}
            ongoingPaymentDateHuman={'11/11/2021'}
            ongoingInterval={'year'}
            coupon={null}
          />
        </Dialog>
        <Dialog title="Coupon handled badly">
          <PaymentInfo
            trialPeriodDays={0}
            currentPaymentDateHuman="today"
            currentPayment={{ cents: 320, currency: 'usd', totalDiscount: 0 }}
            ongoingPayment={{
              cents: 1000,
              currency: 'usd',
              totalDiscount: 500,
            }}
            ongoingInterval={'year'}
            ongoingPaymentDateHuman={'11/11/2021'}
            coupon={{
              name: 'foo',
              variesWithTime: true,
              explanation: 'whatever',
            }}
          />
        </Dialog>
      </Rows>
    </DisableDialogLayer_PlaygroundOnly.Provider>
  );
}

const toastQueue = new ToastQueue();

const CanvasPlayground = observer(function _CanvasPlayground() {
  const [viewport] = React.useState(() => new CanvasViewport(() => {}, true));
  const aRef = React.useRef<HTMLDivElement>(null);
  const bRef = React.useRef<HTMLDivElement>(null);
  const boxes = [
    [aRef, 'A'],
    [bRef, 'B'],
  ] as const;

  return (
    <Rows spacing={16}>
      <PlaygroundSectionTitle label="Canvas" />
      <Inline spacing={16}>
        {boxes.map(([ref, label]) => (
          <Button
            key={label}
            label={`Ensure ${label} visible`}
            onClick={() => viewport.ensureVisible(ref.current!)}
            size="small"
          />
        ))}
      </Inline>
      <div className={styles.canvasContainer}>
        <Canvas viewport={viewport}>
          <div ref={aRef} className={styles.boxA} />
          <div ref={bRef} className={styles.boxB} />
        </Canvas>
      </div>
    </Rows>
  );
});

function AiToastsPlayground() {
  const [[taskLog, feedbackLayer]] = React.useState(() => {
    const taskLog = new AiTaskLog(
      new UndoManagerImpl(
        projectDomain,
        EMPTY_PROJECT,
        new CounterClock(),
        () => undefined,
      ),
      new LLMErrorFormatter({ showAuthWall: undefined }),
    );
    const { feedbackLayer } = installAiToasts(
      {
        postFeedback: async (...args) => {
          alert('postFeedback: ' + JSON.stringify(args));
        },
      },
      taskLog,
      toastQueue,
      'logged-in',
    );
    return [taskLog, feedbackLayer] as const;
  });
  return (
    <Rows spacing={16}>
      <PlaygroundSectionTitle label="Ai Toasts" />
      <StoppableSpinner onStop={() => alert('stopped')} />
      <Button
        onClick={() =>
          taskLog.startTask(
            {
              titleWhenRunning: `Faking ${Math.random()}...`,
              icon: IconCopyAi,
              thirdPersonTooltip: 'fake task',
              errorTooltip: 'fake task',
              affects: [],
            },
            (ctx) => {
              ctx.onGetUsageId(123);
              return delay(2_000);
            },
          )
        }
        label="Run fake AI task"
      />
      <Button
        onClick={() => showAllPagesCelebrationToast(toastQueue)}
        label="Show all pages celebration toast"
      />
      {feedbackLayer}
    </Rows>
  );
}

function GrowthNudgesPlayground() {
  return <GrowthNudge onClick={() => {}} onDismiss={() => {}} />;
}

function FeatureGatePlayground() {
  return (
    <DisableDialogLayer_PlaygroundOnly.Provider value={true}>
      <Rows spacing={16}>
        <PlaygroundSectionTitle label="Feature Gates" />
        <Inline spacing={16}>
          <ProFeatureGateDialog
            currentPlan={PlanName.FREE}
            initialFeatureKey="projects"
            onClose={() => {}}
            analytics={analytics}
          />
          <TeamFeatureGateDialog onClose={() => {}} analytics={analytics} />
        </Inline>
      </Rows>
    </DisableDialogLayer_PlaygroundOnly.Provider>
  );
}

function InteractiveSitemapImportPlayground() {
  const [importingState, setImportingState] = React.useState<
    'starting' | 'importing' | 'done'
  >('starting');

  return (
    <>
      <PlaygroundSectionTitle label="Interactive importing dialog" />
      {importingState === 'starting' && (
        <ImportingDialogView kind="starting" domain="www.example.com" />
      )}
      {importingState === 'importing' && (
        <ImportingDialogView
          kind="importing"
          domain="www.example.com"
          currentPageIndex={0}
          totalPages={10}
          pageLimitReached={false}
        />
      )}
      {importingState === 'done' && (
        <ImportingDialogView
          kind="done"
          domain="www.example.com"
          totalPages={10}
          pageLimitReached={false}
        />
      )}
      <Button
        onClick={() =>
          setImportingState((importingState) =>
            importingState === 'starting'
              ? 'importing'
              : importingState === 'importing'
              ? 'done'
              : 'starting',
          )
        }
      >
        Toggle importing state
      </Button>
    </>
  );
}

function SitemapImportPlayground() {
  return (
    <DisableDialogLayer_PlaygroundOnly.Provider value={true}>
      <PlaygroundSectionTitle label="Importing dialog states" />
      <ImportingDialogView kind="starting" domain="www.example.com" />
      <ImportingDialogView
        kind="importing"
        domain="www.guzmanygomez.com.au"
        currentPageIndex={0}
        totalPages={10}
        pageLimitReached={false}
      />
      <ImportingDialogView
        kind="importing"
        domain="stackoverflow.com"
        currentPageIndex={49}
        totalPages={50}
        pageLimitReached={true}
      />
      <ImportingDialogView
        kind="done"
        domain="stackoverflow.com"
        totalPages={50}
        pageLimitReached={true}
      />
    </DisableDialogLayer_PlaygroundOnly.Provider>
  );
}

// This isn't actually used in blocks anywhere, but this component contains
// images that are served from prod and included in the extensions, so we
// include it here to ensure it gets bundled.
// TODO(anyone): Rethink asset serving for extensions.
function ExternalAssetPlayground() {
  return <NoProjectsEmptyState />;
}

export function Playground() {
  return (
    <div className={styles.playground}>
      <Rows spacing={32}>
        <Title5>Playground</Title5>
        <VariantsPlayground />
        <SignupFormPlayground />
        <OnboardingPlayground />
        <SearchPlayground />
        <KeyboardShortcutsDialogPlayground />
        <CommentingPlayground />
        <SurveyPlayground />
        <ManageAccountPlayground />
        <ProjectsListPlayground />
        <CancelPlanDialogPlayground />
        <PlansPlayground />
        <CanvasPlayground />
        <AiToastsPlayground />
        <GrowthNudgesPlayground />
        <FeatureGatePlayground />
        <ExternalAssetPlayground />
        <ToastLayer queue={toastQueue} />
        <SitemapImportPlayground />
      </Rows>
    </div>
  );
}
